import { friendlyError } from '../../../utils/userMessages';
import { resolveCompanyId, normalizeCompanyRecord, getCompanyLogoUrl, getCompanyCoverUrl, getPersonPhotoUrl } from '../../../utils/companyIdentity';
import { brandPalette as palette, getReadableColor, getContrastText } from '../../../utils/brandPalette';
import { compactList, userBusinessProfile } from '../../../utils/businessProfile';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Avatar,
    Chip,
    Divider,
    IconButton,
    Tooltip,
    Skeleton,
    Stack,
    Badge,
    LinearProgress,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    ListItemSecondaryAction,
    Tabs,
    Tab,
    Alert,
    CircularProgress,
    CardMedia,
    Fade,
    Zoom,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Business as BusinessIcon,
    LocationOn as LocationIcon,
    CheckCircle as CheckCircleIcon,
    Block as BlockIcon,
    Verified as VerifiedIcon,
    People as PeopleIcon,
    Event as EventIcon,
    Schedule as ScheduleIcon,
    Chat as ChatIcon,
    Send as SendIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Language as LanguageIcon,
    LinkedIn as LinkedInIcon,
    Facebook as FacebookIcon,
    Twitter as TwitterIcon,
    Public as PublicIcon,
    Star as StarIcon,
    Assessment as AssessmentIcon,
    CalendarToday as CalendarIcon,
    Schedule as AccessTimeIcon,
    FiberManualRecord as LiveIcon,
    AutoAwesome as AiIcon,
    Share as ShareIcon,
    Description as CopyIcon,
    Storefront as StorefrontIcon,
    Group as GroupIcon,
    Analytics as AnalyticsIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Info as InfoIcon,
    Close as CloseIcon,
    Edit as EditIcon,
    Menu as MoreVertIcon,
    Pending as PendingIcon,
    Visibility as VisibilityIcon,
    Cancel as CancelIcon,
    Flag as FlagIcon,
    Stars as StarsIcon,
    Diamond as DiamondIcon,
    Handshake as HandshakeIcon,
    Image as ImageIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../auth/AuthContext';
import { db } from '../../../config/firebase';
import { 
    doc, 
    getDoc, 
    collection, 
    query, 
    where, 
    getDocs,
    addDoc,
    serverTimestamp 
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import { safeExternalUrl } from '../../../utils/safeNavigation';
import Header from '../../common/Header/Header';
import EntityRatingButton from '../../common/EntityRatingButton';
import LoadingSpinner from '../../common/LoadingSpinner';
import { analyzeMatch } from '../../../services/integrations/ai';
import './CompanyProfile.css';

// ============================================
// DESIGN TOKENS
// ============================================



const fontImport = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,500&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
`;

// ============================================
// ANIMATED SECTION
// ============================================

const AnimatedSection = ({ children, delay = 0 }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: '100%', width: '100%' }}
        >
            {children}
        </motion.div>
    );
};

const SectionRule = ({ color = palette.rust }) => (
    <Box
        sx={{
            width: 34,
            height: 3,
            borderRadius: 2,
            background: `linear-gradient(90deg, ${color}, transparent)`,
            mt: 0.75,
            animation: 'shimmerRule 3.2s ease-in-out infinite',
        }}
    />
);

const paperCardSx = {
    p: { xs: 3, md: 4 },
    borderRadius: '14px',
    border: `1px solid ${palette.hairline}`,
    boxShadow: '0 14px 34px rgba(25,57,78,.075), 0 3px 9px rgba(25,57,78,.03), inset 0 1px 0 rgba(255,255,255,.92)',
    background: 'linear-gradient(145deg, rgba(255,255,255,.96), rgba(248,251,253,.88))',
    backdropFilter: 'blur(14px) saturate(122%)',
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    boxSizing: 'border-box',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '2px',
        background: `linear-gradient(90deg, ${palette.rust}, ${palette.gold}, ${palette.pine}, ${palette.purple}, ${palette.rust})`,
        backgroundSize: '300% 100%',
        opacity: 0.5,
        animation: 'shimmerEdge 10s linear infinite',
        pointerEvents: 'none',
    },
};

// ============================================
// STATUS CHIP
// ============================================

const StatusChip = ({ status }) => {
    const statusConfig = {
        active: { label: 'Active', color: getReadableColor(palette.pine), icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
        blocked: { label: 'Blocked', color: palette.clay, icon: <BlockIcon sx={{ fontSize: 14 }} /> },
        pending: { label: 'Pending', color: getReadableColor(palette.gold), icon: <ScheduleIcon sx={{ fontSize: 14 }} /> },
        verified: { label: 'Verified', color: getReadableColor(palette.pine), icon: <VerifiedIcon sx={{ fontSize: 14 }} /> }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
        <Chip
            icon={config.icon}
            label={config.label}
            size="small"
            sx={{
                bgcolor: `${config.color}15`,
                color: config.color,
                fontWeight: 600,
                height: 24,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                '& .MuiChip-icon': { color: config.color }
            }}
        />
    );
};

// ============================================
// INFO CARD COMPONENT
// ============================================

const InfoCard = ({ icon, label, value, color, iconBg }) => (
    <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2, 
        p: 2, 
        borderRadius: '12px', 
        bgcolor: palette.canvasAlt,
        border: `1px solid ${palette.hairline}`,
        transition: 'all 0.3s ease',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(33,26,20,0.06)',
            borderColor: color,
        }
    }}>
        <Box sx={{
            width: 36,
            height: 36,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: iconBg || `${color}12`,
            color: color,
            flexShrink: 0,
        }}>
            {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: palette.inkSoft, display: 'block', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {label}
            </Typography>
            <Typography variant="body2" fontWeight={600} noWrap sx={{ color: palette.ink }}>
                {value || 'N/A'}
            </Typography>
        </Box>
    </Box>
);

// ============================================
// PAGE LOADER
// ============================================

const PageLoader = () => (
    <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: palette.canvas,
        zIndex: 9999,
    }}>
        <style>{fontImport}</style>
        <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
            <Box sx={{ position: 'relative', width: 100, height: 100 }}>
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                    style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
                >
                    <Box sx={{
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        border: `3px solid ${palette.hairline}`,
                        borderTop: `3px solid ${palette.rust}`,
                        borderRight: `3px solid ${palette.gold}`,
                        borderBottom: `3px solid ${palette.pine}`,
                        borderLeft: `3px solid ${palette.purple}`,
                        animation: 'spin 2s linear infinite',
                    }} />
                </motion.div>
                <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                    style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
                >
                    <BusinessIcon sx={{ fontSize: 36, color: getReadableColor(palette.rust) }} />
                </motion.div>
            </Box>
        </motion.div>
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            style={{ textAlign: 'center', marginTop: '1.5rem' }}
        >
            <Typography sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 700, fontSize: '1.8rem', color: palette.ink }}>
                <span style={{ color: getReadableColor(palette.rust) }}>TELCO</span>
                <span style={{ color: getReadableColor(palette.gold) }}>FAIR</span>
            </Typography>
            <Box sx={{ width: 200, height: 3, bgcolor: palette.hairline, borderRadius: 2, mt: 2, mx: 'auto', overflow: 'hidden' }}>
                <motion.div
                    initial={{ width: '0%' }}
                    animate={{ width: '100%' }}
                    transition={{ duration: 1.5, ease: 'easeInOut' }}
                    style={{
                        height: '100%',
                        background: `linear-gradient(90deg, ${palette.rust}, ${palette.gold}, ${palette.pine}, ${palette.purple})`,
                        borderRadius: 2,
                    }}
                />
            </Box>
        </motion.div>
        <style>{`
            @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `}</style>
    </Box>
);

// ============================================
// MAIN COMPANY PROFILE
// ============================================

const CompanyProfile = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const { userData, companyData, currentUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [company, setCompany] = useState(null);
    const [tabValue, setTabValue] = useState(0);
    const [meetings, setMeetings] = useState([]);
    const [error, setError] = useState(null);
    const [invitationStatus, setInvitationStatus] = useState(null);
    const [associatedUsers, setAssociatedUsers] = useState([]);
    const [companyMatch, setCompanyMatch] = useState(null);

    // ============================================
    // LOAD COMPANY DATA - FIXED
    // ============================================

    const loadCompanyData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            console.log('🔍 CompanyProfile - Starting data fetch...');
            console.log('📦 companyData from context:', companyData);
            console.log('👤 userData from context:', userData);
            
            let companyDataObj = null;
            let companyId = resolveCompanyId({
                routeId: id,
                locationCompany: location.state?.company,
                companyData,
                userData,
                currentUser,
            });

            // 1. Check if company data is passed via location state
            if (location.state?.company) {
                companyDataObj = normalizeCompanyRecord(location.state.company, companyId);
                companyId = companyDataObj?.id || companyId;
                console.log('✅ Using company from location state');
            }

            // 2. If no companyId from location, try to get from URL params
            if (!companyId) {
                const pathParts = window.location.pathname.split('/');
                const lastPart = pathParts[pathParts.length - 1];
                if (lastPart && lastPart !== 'company' && lastPart !== 'profile') {
                    companyId = lastPart;
                    console.log('🔍 Company ID from URL:', companyId);
                }
            }

            // 3. Try to get company from context if no companyDataObj yet
            if (!companyDataObj && companyData) {
                const contextCompany = normalizeCompanyRecord(companyData);
                if (!companyId || contextCompany?.id === companyId || contextCompany?.companyId === companyId) {
                    companyDataObj = contextCompany;
                    companyId = contextCompany?.id || companyId;
                    console.log('✅ Using company from context');
                }
            }

            // 4. If still no company data, fetch from Firestore
            if (!companyDataObj && companyId) {
                console.log('🔍 Fetching company from Firestore:', companyId);
                const companyDoc = await getDoc(doc(db, 'companies', companyId));
                if (companyDoc.exists()) {
                    companyDataObj = normalizeCompanyRecord(companyDoc.data(), companyDoc.id);
                    console.log('✅ Company found in Firestore');
                } else {
                    throw new Error('Company not found');
                }
            }

            // 5. If still no company data, try to find by company name in userData
            if (!companyDataObj && userData?.companyName) {
                console.log('🔍 Looking for company in userData:', userData.companyName);
                const companiesRef = collection(db, 'companies');
                const q = query(companiesRef, where('companyName', '==', userData.companyName));
                const querySnapshot = await getDocs(q);
                if (!querySnapshot.empty) {
                    const doc = querySnapshot.docs[0];
                    companyDataObj = normalizeCompanyRecord(doc.data(), doc.id);
                    companyId = companyDataObj.id;
                    console.log('✅ Company found by name in userData');
                }
            }

            if (!companyDataObj) {
                throw new Error('Company not found');
            }

            // Normalize data: ensure we have primary fields from services/regions arrays if needed
            if (companyDataObj && !companyDataObj.primaryService && companyDataObj.services && companyDataObj.services.length > 0) {
                companyDataObj.primaryService = companyDataObj.services[0];
                companyDataObj.secondaryService = companyDataObj.services[1] || '';
            }

            if (companyDataObj && !companyDataObj.primaryRegion && companyDataObj.regions && companyDataObj.regions.length > 0) {
                companyDataObj.primaryRegion = companyDataObj.regions[0];
                companyDataObj.secondaryRegion = companyDataObj.regions[1] || '';
            }

            if (companyDataObj && !companyDataObj.primaryConnectWith && companyDataObj.connectWith && companyDataObj.connectWith.length > 0) {
                companyDataObj.primaryConnectWith = companyDataObj.connectWith[0];
                companyDataObj.secondaryConnectWith = companyDataObj.connectWith[1] || '';
            }

            setCompany(companyDataObj);

            const userCompanyId = resolveCompanyId({ companyData, userData, currentUser });
            if (userCompanyId && userCompanyId !== companyId) {
                try {
                    const analysis = await analyzeMatch({ companyId: userCompanyId, targetCompanyId: companyId });
                    setCompanyMatch(analysis?.matchAnalysis || null);
                } catch (matchError) {
                    console.warn('Company business-fit analysis unavailable:', matchError);
                    setCompanyMatch(null);
                }
            } else {
                setCompanyMatch(null);
            }

            // Load associated users for the detailed company view. Support both the
            // canonical companyId field and legacy companyName-only user documents.
            try {
                const usersRef = collection(db, 'users');
                const userQueries = [
                    query(usersRef, where('companyId', '==', companyId)),
                    query(usersRef, where('companyName', '==', companyDataObj.companyName || '')),
                ];
                const userResults = await Promise.allSettled(userQueries.map((userQuery) => getDocs(userQuery)));
                const userMap = new Map();
                userResults.forEach((result) => {
                    if (result.status !== 'fulfilled') return;
                    result.value.forEach((userDoc) => {
                        const record = { id: userDoc.id, ...userDoc.data() };
                        if (record.isBlocked === true || record.isActive === false) return;
                        userMap.set(userDoc.id, record);
                    });
                });
                setAssociatedUsers(Array.from(userMap.values()).sort((a, b) => {
                    const nameA = String(a.displayName || a.name || [a.firstName, a.lastName].filter(Boolean).join(' ') || a.email || '');
                    const nameB = String(b.displayName || b.name || [b.firstName, b.lastName].filter(Boolean).join(' ') || b.email || '');
                    return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
                }));
            } catch (userError) {
                console.warn('Unable to load associated company users:', userError);
                setAssociatedUsers([]);
            }

            // Fetch meetings for this company. Support both canonical and legacy fields
            // so existing documents remain visible without a migration.
            const meetingsRef = collection(db, 'meetings');
            const meetingQueries = [
                query(meetingsRef, where('targetId', '==', companyId)),
                query(meetingsRef, where('targetCompanyId', '==', companyId)),
                query(meetingsRef, where('requesterId', '==', companyId)),
                query(meetingsRef, where('companyId', '==', companyId)),
            ];
            const meetingSnapshots = await Promise.all(meetingQueries.map((meetingQuery) => getDocs(meetingQuery)));
            const meetingMap = new Map();
            meetingSnapshots.forEach((snapshot) => {
                snapshot.forEach((meetingDoc) => {
                    meetingMap.set(meetingDoc.id, { id: meetingDoc.id, ...meetingDoc.data() });
                });
            });
            setMeetings(Array.from(meetingMap.values()));

            // Check invitation status between user's company and this company
            if (userCompanyId && userCompanyId !== companyId) {
                const invitationsRef = collection(db, 'invitations');
                
                const sentQuery = query(
                    invitationsRef,
                    where('companyId', '==', userCompanyId),
                    where('targetCompanyId', '==', companyId)
                );
                const sentSnapshot = await getDocs(sentQuery);
                
                const receivedQuery = query(
                    invitationsRef,
                    where('companyId', '==', companyId),
                    where('targetCompanyId', '==', userCompanyId)
                );
                const receivedSnapshot = await getDocs(receivedQuery);
                
                if (!sentSnapshot.empty) {
                    setInvitationStatus({ 
                        status: ['accepted', 'connected'].includes(sentSnapshot.docs[0].data().status) ? 'accepted' : sentSnapshot.docs[0].data().status, 
                        type: 'sent',
                        id: sentSnapshot.docs[0].id
                    });
                } else if (!receivedSnapshot.empty) {
                    setInvitationStatus({ 
                        status: ['accepted', 'connected'].includes(receivedSnapshot.docs[0].data().status) ? 'accepted' : receivedSnapshot.docs[0].data().status, 
                        type: 'received',
                        id: receivedSnapshot.docs[0].id
                    });
                }
            }

        } catch (error) {
            console.error('Error fetching company:', error);
            setError(friendlyError(error, 'Unable to complete this request.'));
            toast.error('Failed to load company details');
        } finally {
            setLoading(false);
        }
    };

    // ============================================
    // EFFECT
    // ============================================

    useEffect(() => {
        loadCompanyData();
    }, [id, location.state, userData, companyData, currentUser]);

    // ============================================
    // HANDLERS
    // ============================================

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleGoBack = () => {
        navigate('/dashboard/companies');
    };

    const handleSendInvite = async () => {
        if (!company) return;
        
        try {
            const userCompanyId = resolveCompanyId({ companyData, userData, currentUser });
            const userCompanyName = userData?.companyName || companyData?.companyName || 'Unknown Company';
            
            const invitationData = {
                companyId: userCompanyId,
                companyName: userCompanyName,
                targetCompanyId: company.id,
                targetCompanyName: company.companyName,
                status: 'pending',
                sentAt: serverTimestamp(),
                expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            };

            await addDoc(collection(db, 'invitations'), invitationData);
            toast.success(`Invitation sent to ${company.companyName}!`);
            setInvitationStatus({ status: 'pending', type: 'sent' });
            
            // Refresh the data
            await loadCompanyData();
            
        } catch (error) {
            console.error('Error sending invitation:', error);
            toast.error('Failed to send invitation');
        }
    };

    const handleChat = () => {
        toast.success(`Opening chat with ${company?.companyName}`);
        navigate('/dashboard/messages', { state: { company } });
    };

    const handleSchedule = () => {
        if (!['accepted', 'connected'].includes(invitationStatus?.status)) {
            toast.error('Please accept the invitation first to schedule a meeting');
            return;
        }
        navigate('/dashboard/meetings/schedule', { state: { company } });
    };

    // ============================================
    // EXTRACT COMPANY FIELDS
    // ============================================

    const companyName = company?.companyName || 'Unnamed Company';
    const companyType = company?.companyType?.replace(/_/g, ' ') || company?.companyType || 'N/A';
    const primaryService = company?.primaryService || company?.primary_service || 'N/A';
    const secondaryService = company?.secondaryService || company?.secondary_service || 'N/A';
    const primaryRegion = company?.primaryRegion || company?.primary_region || 'N/A';
    const secondaryRegion = company?.secondaryRegion || company?.secondary_region || 'N/A';
    const primaryConnectWith = company?.primaryConnectWith || company?.primary_connect_with || 'N/A';
    const secondaryConnectWith = company?.secondaryConnectWith || company?.secondary_connect_with || 'N/A';
    const otherConnectWith = company?.otherConnectWith || company?.other_connect_with || '';
    const country = company?.country || 'N/A';
    const countryCode = company?.countryCode || company?.country_code || '';
    const dialCode = company?.dialCode || company?.dial_code || '';
    const email = company?.email || 'N/A';
    const phone = company?.phone || 'N/A';
    const website = company?.website || '';
    const description = company?.description || 'No description provided for this company.';
    const logo = getCompanyLogoUrl(company || {}) || null;
    const coverImage = getCompanyCoverUrl(company || {}) || null;

    // For backward compatibility with old field names
    const services = company?.services || [];
    const regions = company?.regions || [];
    const yearFounded = company?.yearFounded || company?.year_founded || '';
    const companySize = company?.companySize || company?.company_size || 'N/A';
    const linkedin = company?.linkedin || '';

    console.log('📊 CompanyProfile - Parsed Company Data:');
    console.log('  - companyName:', companyName);
    console.log('  - companyType:', companyType);
    console.log('  - primaryService:', primaryService);
    console.log('  - secondaryService:', secondaryService);
    console.log('  - primaryRegion:', primaryRegion);
    console.log('  - secondaryRegion:', secondaryRegion);
    console.log('  - primaryConnectWith:', primaryConnectWith);
    console.log('  - secondaryConnectWith:', secondaryConnectWith);
    console.log('  - otherConnectWith:', otherConnectWith);
    console.log('  - country:', country);
    console.log('  - countryCode:', countryCode);
    console.log('  - dialCode:', dialCode);
    console.log('  - email:', email);
    console.log('  - phone:', phone);
    console.log('  - website:', website);
    console.log('  - hasLogo:', !!logo);
    console.log('  - hasCover:', !!coverImage);

    // Generate cover gradient if no cover image
    const coverGradient = 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)';

    // ============================================
    // RENDER
    // ============================================

    const canvasSx = {
        minHeight: '100vh',
        position: 'relative',
        bgcolor: palette.canvas,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        backgroundImage: `
            radial-gradient(circle at 8% 0%, rgba(193,84,31,0.10), transparent 42%),
            radial-gradient(circle at 96% 8%, rgba(47,89,66,0.08), transparent 40%),
            radial-gradient(circle at 50% 100%, rgba(184,132,42,0.06), transparent 45%)
        `,
        '&::before': {
            content: '""',
            position: 'fixed',
            inset: 0,
            pointerEvents: 'none',
            opacity: 0.5,
            backgroundImage:
                'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27120%27 height=%27120%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.9%27 numOctaves=%272%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.035%27/%3E%3C/svg%3E")',
        },
    };

    if (loading) {
        return <LoadingSpinner label="Loading company profile…" />;
    }

    if (error || !company) {
        return (
            <Box className="erp-page-shell erp-internal-page" sx={canvasSx}>
                <style>{fontImport}</style>
                <Header />
                <Container maxWidth={false} disableGutters sx={{ py: 6, px: { xs: 2, sm: 4, md: 6, lg: 8 }, position: 'relative', width: '100%' }}>
                    <Paper elevation={0} sx={paperCardSx}>
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <BusinessIcon sx={{ fontSize: 64, color: palette.inkSoft, opacity: 0.4 }} />
                            <Typography variant="h5" sx={{ color: palette.ink, mt: 2, fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" }}>
                                Company Not Found
                            </Typography>
                            <Typography variant="body2" sx={{ color: palette.inkSoft, mt: 1 }}>
                                {error || 'The company you are looking for does not exist.'}
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<ArrowBackIcon />}
                                onClick={handleGoBack}
                                sx={{
                                    mt: 3,
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                    '&:hover': { transform: 'translateY(-2px)' }
                                }}
                            >
                                Back to Companies
                            </Button>
                        </Box>
                    </Paper>
                </Container>
            </Box>
        );
    }
    const accentColor = palette.rust;
    const accentGradient = `linear-gradient(135deg, ${palette.rust}, ${palette.pine})`;

    return (
        <Box className="erp-page-shell erp-internal-page" sx={canvasSx}>
            <style>{fontImport}</style>
            <Header />

            <Container maxWidth={false} disableGutters sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, sm: 3, md: 4, lg: 6 }, position: 'relative', width: '100%', maxWidth: '1600px', mx: 'auto' }}>
                <Box className="erp-page-hero" sx={{ mb: 2.5, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Box>
                        <Typography className="erp-page-title">Company Profile</Typography>
                        <Typography className="erp-page-subtitle">Company identity, services, connections and meeting activity.</Typography>
                    </Box>
                    <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleGoBack}>Back to Companies</Button>
                </Box>

                {companyMatch && <Paper elevation={0} className="tf-profile-match-command" sx={{ '--match-color': companyMatch.score >= 70 ? '#13A773' : companyMatch.score >= 45 ? '#2F6FED' : '#F39A24', mb: 2.2 }}>
                    <Box className="tf-profile-match-art"><AiIcon /></Box>
                    <Grid container spacing={1.4} sx={{ alignItems: 'center', position: 'relative', zIndex: 2 }}>
                        <Grid size={{ xs: 12, md: 'auto' }}><Box className="tf-profile-match-score"><Typography sx={{ fontSize: 28, lineHeight: 1, fontWeight: 950 }}>{Math.round(Number(companyMatch.score || 0))}%</Typography><Typography sx={{ mt: .25, fontSize: 8.5, fontWeight: 900, letterSpacing: '.06em' }}>COMPANY FIT</Typography></Box></Grid>
                        <Grid  size={{ xs: 12, md: 'grow' }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 950, color: '#1C2A3D' }}>{companyMatch.level || 'AI business compatibility'}</Typography>
                            <Typography sx={{ mt: .25, fontSize: 10.7, color: '#718096' }}>Calculated from your specialization, primary and secondary services, service regions and buying needs against what this company offers and is looking for.</Typography>
                            <Stack direction="row" spacing={.55} useFlexGap sx={[{ flexWrap: 'wrap' }, { mt: .75 }]}>{(companyMatch.reasons || []).slice(0, 3).map((reason, index) => <Chip key={index} size="small" icon={<AiIcon />} label={reason} sx={{ height: 24, maxWidth: 340, bgcolor: 'rgba(255,255,255,.76)', border: '1px solid rgba(31,62,82,.08)', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />)}</Stack>
                        </Grid>
                        <Grid size={{ xs: 12, md: 'auto' }}><Button variant="contained" startIcon={<PeopleIcon />} onClick={() => setTabValue(4)} sx={{ minHeight: 38, fontWeight: 900, borderRadius: '10px' }}>View People & Send Intro</Button></Grid>
                    </Grid>
                </Paper>}

                {/* Company Header with Cover Image */}
                <Paper elevation={0} sx={{ ...paperCardSx, mb: 4, p: 0, overflow: 'hidden' }}>
                    {/* Cover Image */}
                    <Box sx={{ position: 'relative', height: { xs: 120, md: 140 }, width: '100%' }}>
                        {coverImage ? (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundImage: `url(${coverImage})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    transition: 'opacity .25s ease'
                                }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    background: coverGradient,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <ImageIcon sx={{ fontSize: 60, color: 'rgba(255,255,255,0.3)' }} />
                            </Box>
                        )}
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: '40%',
                                background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.3))',
                            }}
                        />
                        {/* Status Badge on Cover */}
                        <Box sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            zIndex: 2,
                            display: 'flex',
                            gap: 0.5,
                            flexWrap: 'wrap',
                        }}>
                            {company.isVerified && (
                                <Tooltip title="Verified Company">
                                    <VerifiedIcon sx={{ color: getReadableColor(palette.pine), fontSize: 20, bgcolor: 'rgba(255,255,255,0.9)', borderRadius: '50%', p: 0.5 }} />
                                </Tooltip>
                            )}
                            <StatusChip status={company.isBlocked ? 'blocked' : company.isActive ? 'active' : 'pending'} />
                        </Box>
                    </Box>

                    {/* Company Info */}
                    <Box sx={{ p: { xs: 3, md: 4 }, pt: 0 }}>
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: { xs: 'flex-start', md: 'center' },
                            gap: 3,
                            position: 'relative',
                            mt: -3,
                        }}>
                            <Avatar
                                sx={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: '14px',
                                    background: accentGradient,
                                    boxShadow: `0 8px 24px ${accentColor}40`,
                                    fontWeight: 700,
                                    fontSize: '2rem',
                                    fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                                    color: '#fff',
                                    border: `4px solid ${palette.paper}`,
                                }}
                            >
                                {logo ? (
                                    <img src={logo} alt={companyName} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '14px' }} />
                                ) : (
                                    companyName?.charAt(0) || 'C'
                                )}
                            </Avatar>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                                            fontWeight: 700,
                                            color: palette.ink,
                                            fontSize: { xs: '1.8rem', md: '2.2rem' },
                                            letterSpacing: '-0.01em',
                                        }}
                                    >
                                        {companyName}
                                    </Typography>
                                    {company.isVerified && (
                                        <Tooltip title="Verified Company">
                                            <VerifiedIcon sx={{ color: getReadableColor(palette.pine), fontSize: 28 }} />
                                        </Tooltip>
                                    )}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                                    {country && country !== 'N/A' && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: palette.inkSoft }}>
                                            <LocationIcon sx={{ fontSize: 16 }} />
                                            <Typography variant="body2">{country}</Typography>
                                        </Box>
                                    )}
                                    <Chip
                                        label={companyType}
                                        size="small"
                                        sx={{
                                            bgcolor: `${accentColor}12`,
                                            color: accentColor,
                                            fontWeight: 600,
                                            textTransform: 'capitalize'
                                        }}
                                    />
                                    {primaryService && primaryService !== 'N/A' && (
                                        <Chip
                                            label={`Service: ${primaryService}`}
                                            size="small"
                                            sx={{
                                                bgcolor: `${palette.gold}12`,
                                                color: getReadableColor(palette.gold),
                                                fontWeight: 500
                                            }}
                                        />
                                    )}
                                    {companySize && companySize !== 'N/A' && (
                                        <Chip
                                            label={companySize}
                                            size="small"
                                            sx={{
                                                bgcolor: 'rgba(33,26,20,0.05)',
                                                color: palette.inkSoft,
                                                fontWeight: 500
                                            }}
                                        />
                                    )}
                                </Box>
                            </Box>

                            {/* Action Buttons */}
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {invitationStatus?.status === 'accepted' ? (
                                    <>
                                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                            <Button
                                                variant="outlined"
                                                startIcon={<ChatIcon />}
                                                onClick={handleChat}
                                                sx={{
                                                    borderRadius: '12px',
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    borderColor: palette.gold,
                                                    color: getReadableColor(palette.gold),
                                                    '&:hover': { 
                                                        bgcolor: `${palette.gold}0f`,
                                                        transform: 'translateY(-2px)'
                                                    }
                                                }}
                                            >
                                                Chat
                                            </Button>
                                        </motion.div>
                                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                            <Button
                                                variant="contained"
                                                startIcon={<EventIcon />}
                                                onClick={handleSchedule}
                                                sx={{
                                                    borderRadius: '12px',
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                                    boxShadow: '0 4px 16px rgba(193,84,31,0.3)',
                                                    '&:hover': {
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: '0 8px 24px rgba(193,84,31,0.4)'
                                                    }
                                                }}
                                            >
                                                Schedule Meeting
                                            </Button>
                                        </motion.div>
                                    </>
                                ) : invitationStatus?.status === 'pending' ? (
                                    <Chip
                                        icon={<PendingIcon sx={{ fontSize: 14 }} />}
                                        label="Invitation Pending"
                                        sx={{
                                            bgcolor: `${palette.gold}15`,
                                            color: getReadableColor(palette.gold),
                                            fontWeight: 600,
                                            fontSize: '0.85rem',
                                            py: 2,
                                        }}
                                    />
                                ) : (
                                    <>
                                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                            <Button
                                                variant="outlined"
                                                startIcon={<SendIcon />}
                                                onClick={handleSendInvite}
                                                sx={{
                                                    borderRadius: '12px',
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    borderColor: palette.hairline,
                                                    color: palette.ink,
                                                    '&:hover': { 
                                                        borderColor: palette.teal,
                                                        bgcolor: `${palette.teal}0f`,
                                                        transform: 'translateY(-2px)'
                                                    }
                                                }}
                                            >
                                                Connect
                                            </Button>
                                        </motion.div>
                                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                            <Button
                                                variant="contained"
                                                startIcon={<AiIcon />}
                                                sx={{
                                                    borderRadius: '12px',
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    background: `linear-gradient(135deg, ${palette.purple}, ${palette.navy})`,
                                                    boxShadow: '0 4px 16px rgba(106,27,154,0.3)',
                                                    '&:hover': {
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: '0 8px 24px rgba(106,27,154,0.4)'
                                                    }
                                                }}
                                                onClick={() => {
                                                    toast.info(`AI analysis for ${companyName}`);
                                                }}
                                            >
                                                AI Connect
                                            </Button>
                                        </motion.div>
                                    </>
                                )}
                                <EntityRatingButton targetType="company" targetId={company.id || company.companyId} targetName={companyName} target={company} color="#E9A220" />
                                <IconButton
                                    sx={{ 
                                        color: palette.inkSoft,
                                        '&:hover': { bgcolor: 'rgba(33,26,20,0.05)' }
                                    }}
                                >
                                    <MoreVertIcon />
                                </IconButton>
                            </Box>
                        </Box>
                    </Box>
                </Paper>

                {/* Stats Cards - Updated with registration fields */}
                <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' }, 
                    gap: 3,
                    width: '100%',
                    mb: 4,
                }}>
                    {/* Company Type Card */}
                    <motion.div whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
                        <Card elevation={0} sx={{
                            borderRadius: '16px',
                            border: `1px solid ${palette.hairline}`,
                            background: palette.paper,
                            height: '100%',
                            minHeight: 120,
                            display: 'flex',
                            flexDirection: 'column',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 24px rgba(33,26,20,0.08)',
                                borderColor: palette.rust,
                            }
                        }}>
                            <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                                <Box sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                    color: '#fff',
                                    mb: 1.5,
                                    boxShadow: `0 4px 12px ${palette.rust}30`,
                                }}>
                                    <BusinessIcon sx={{ fontSize: 22 }} />
                                </Box>
                                <Typography variant="body2" fontWeight={700} sx={{ color: palette.ink, fontSize: '0.9rem' }}>
                                    {companyType}
                                </Typography>
                                <Typography variant="caption" sx={{ color: palette.inkSoft, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.04em', mt: 0.25 }}>
                                    Company Type
                                </Typography>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Primary Service Card */}
                    <motion.div whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
                        <Card elevation={0} sx={{
                            borderRadius: '16px',
                            border: `1px solid ${palette.hairline}`,
                            background: palette.paper,
                            height: '100%',
                            minHeight: 120,
                            display: 'flex',
                            flexDirection: 'column',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 24px rgba(33,26,20,0.08)',
                                borderColor: palette.gold,
                            }
                        }}>
                            <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                                <Box sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: `linear-gradient(135deg, ${palette.gold}, ${palette.goldLight})`,
                                    color: '#fff',
                                    mb: 1.5,
                                    boxShadow: `0 4px 12px ${palette.gold}30`,
                                }}>
                                    <StarsIcon sx={{ fontSize: 22 }} />
                                </Box>
                                <Typography variant="body2" fontWeight={700} sx={{ color: palette.ink, fontSize: '0.9rem' }}>
                                    {primaryService}
                                </Typography>
                                <Typography variant="caption" sx={{ color: palette.inkSoft, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.04em', mt: 0.25 }}>
                                    Primary Service
                                </Typography>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Primary Region Card */}
                    <motion.div whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
                        <Card elevation={0} sx={{
                            borderRadius: '16px',
                            border: `1px solid ${palette.hairline}`,
                            background: palette.paper,
                            height: '100%',
                            minHeight: 120,
                            display: 'flex',
                            flexDirection: 'column',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 24px rgba(33,26,20,0.08)',
                                borderColor: palette.teal,
                            }
                        }}>
                            <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                                <Box sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: `linear-gradient(135deg, ${palette.teal}, ${palette.tealLight})`,
                                    color: '#fff',
                                    mb: 1.5,
                                    boxShadow: `0 4px 12px ${palette.teal}30`,
                                }}>
                                    <PublicIcon sx={{ fontSize: 22 }} />
                                </Box>
                                <Typography variant="body2" fontWeight={700} sx={{ color: palette.ink, fontSize: '0.9rem' }}>
                                    {primaryRegion}
                                </Typography>
                                <Typography variant="caption" sx={{ color: palette.inkSoft, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.04em', mt: 0.25 }}>
                                    Primary Region
                                </Typography>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Connect With Card */}
                    <motion.div whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
                        <Card elevation={0} sx={{
                            borderRadius: '16px',
                            border: `1px solid ${palette.hairline}`,
                            background: palette.paper,
                            height: '100%',
                            minHeight: 120,
                            display: 'flex',
                            flexDirection: 'column',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 24px rgba(33,26,20,0.08)',
                                borderColor: palette.pine,
                            }
                        }}>
                            <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                                <Box sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: `linear-gradient(135deg, ${palette.pine}, ${palette.pineSoft})`,
                                    color: '#fff',
                                    mb: 1.5,
                                    boxShadow: `0 4px 12px ${palette.pine}30`,
                                }}>
                                    <HandshakeIcon sx={{ fontSize: 22 }} />
                                </Box>
                                <Typography variant="body2" fontWeight={700} sx={{ color: palette.ink, fontSize: '0.9rem' }}>
                                    {primaryConnectWith}
                                </Typography>
                                <Typography variant="caption" sx={{ color: palette.inkSoft, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.04em', mt: 0.25 }}>
                                    Connect With
                                </Typography>
                            </CardContent>
                        </Card>
                    </motion.div>
                </Box>

                {/* Tabs Section - Full Width */}
                <Paper elevation={0} sx={{ ...paperCardSx, width: '100%' }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, width: '100%' }}>
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            variant="fullWidth"
                            sx={{
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                                    fontSize: '0.85rem',
                                    minHeight: 48,
                                    flex: 1,
                                },
                                '& .Mui-selected': {
                                    color: getReadableColor(palette.rust),
                                },
                                '& .MuiTabs-indicator': {
                                    backgroundColor: palette.rust,
                                }
                            }}
                        >
                            <Tab label="Overview" icon={<InfoIcon />} iconPosition="start" />
                            <Tab label="Services & Regions" icon={<StorefrontIcon />} iconPosition="start" />
                            <Tab label="Connect With" icon={<HandshakeIcon />} iconPosition="start" />
                            <Tab label="Contact" icon={<EmailIcon />} iconPosition="start" />
                            <Tab label={`People (${associatedUsers.length})`} icon={<PeopleIcon />} iconPosition="start" />
                        </Tabs>
                    </Box>

                    {/* Overview Tab */}
                    {tabValue === 0 && (
                        <Box sx={{ width: '100%' }}>
                            <Typography variant="h6" sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, mb: 2 }}>
                                About {companyName}
                            </Typography>
                            <Typography variant="body1" sx={{ color: palette.inkSoft, mb: 3, lineHeight: 1.8 }}>
                                {description}
                            </Typography>

                            <Divider sx={{ my: 3 }} />

                            <Typography variant="h6" sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, mb: 2 }}>
                                Company Details
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <InfoCard
                                        icon={<BusinessIcon fontSize="small" />}
                                        label="Company Type"
                                        value={companyType}
                                        color={palette.rust}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <InfoCard
                                        icon={<FlagIcon fontSize="small" />}
                                        label="Country"
                                        value={country}
                                        color={palette.pine}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <InfoCard
                                        icon={<EmailIcon fontSize="small" />}
                                        label="Email"
                                        value={email}
                                        color={palette.teal}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <InfoCard
                                        icon={<PhoneIcon fontSize="small" />}
                                        label="Phone"
                                        value={dialCode ? `${dialCode} ${phone}` : phone}
                                        color={palette.gold}
                                    />
                                </Grid>
                                {website && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <InfoCard
                                            icon={<LanguageIcon fontSize="small" />}
                                            label="Website"
                                            value={
                                                <a href={safeExternalUrl(website, { allowBlob: false }) || undefined} target="_blank" rel="noopener noreferrer" style={{ color: getReadableColor(palette.rust), textDecoration: 'none' }}>
                                                    {website}
                                                </a>
                                            }
                                            color={palette.purple}
                                        />
                                    </Grid>
                                )}
                                {dialCode && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <InfoCard
                                            icon={<PhoneIcon fontSize="small" />}
                                            label="Dial Code"
                                            value={dialCode}
                                            color={palette.amber}
                                        />
                                    </Grid>
                                )}
                                {companySize && companySize !== 'N/A' && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <InfoCard
                                            icon={<PeopleIcon fontSize="small" />}
                                            label="Company Size"
                                            value={companySize}
                                            color={palette.pine}
                                        />
                                    </Grid>
                                )}
                                {yearFounded && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <InfoCard
                                            icon={<CalendarIcon fontSize="small" />}
                                            label="Year Founded"
                                            value={yearFounded}
                                            color={palette.clay}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    )}

                    {/* Services & Regions Tab */}
                    {tabValue === 1 && (
                        <Box sx={{ width: '100%' }}>
                            <Typography variant="h6" sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, mb: 2 }}>
                                Services
                            </Typography>
                            <Box sx={{ mb: 4 }}>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <InfoCard
                                            icon={<StarsIcon fontSize="small" />}
                                            label="Primary Service"
                                            value={primaryService}
                                            color={palette.gold}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <InfoCard
                                            icon={<DiamondIcon fontSize="small" />}
                                            label="Secondary Service"
                                            value={secondaryService}
                                            color={palette.purple}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            <Divider sx={{ my: 3 }} />

                            <Typography variant="h6" sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, mb: 2 }}>
                                Regions
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <InfoCard
                                        icon={<PublicIcon fontSize="small" />}
                                        label="Primary Region"
                                        value={primaryRegion}
                                        color={palette.teal}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <InfoCard
                                        icon={<PublicIcon fontSize="small" />}
                                        label="Secondary Region"
                                        value={secondaryRegion}
                                        color={palette.pine}
                                    />
                                </Grid>
                            </Grid>

                            {/* Display all services as chips */}
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: palette.inkSoft, mb: 1.5 }}>
                                    All Services
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                    {primaryService !== 'N/A' && (
                                        <Chip
                                            label={primaryService}
                                            sx={{
                                                py: 2,
                                                px: 1,
                                                fontSize: '0.85rem',
                                                fontWeight: 600,
                                                bgcolor: `${palette.gold}10`,
                                                color: getReadableColor(palette.gold),
                                                border: `1px solid ${palette.gold}20`,
                                                borderRadius: '10px',
                                            }}
                                        />
                                    )}
                                    {secondaryService !== 'N/A' && (
                                        <Chip
                                            label={secondaryService}
                                            sx={{
                                                py: 2,
                                                px: 1,
                                                fontSize: '0.85rem',
                                                fontWeight: 600,
                                                bgcolor: `${palette.purple}10`,
                                                color: palette.purple,
                                                border: `1px solid ${palette.purple}20`,
                                                borderRadius: '10px',
                                            }}
                                        />
                                    )}
                                    {/* Legacy support for services array */}
                                    {services.length > 0 && services.map((service, index) => (
                                        <Chip
                                            key={`legacy-${index}`}
                                            label={service}
                                            sx={{
                                                py: 2,
                                                px: 1,
                                                fontSize: '0.85rem',
                                                fontWeight: 600,
                                                bgcolor: `${accentColor}10`,
                                                color: accentColor,
                                                border: `1px solid ${accentColor}20`,
                                                borderRadius: '10px',
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>

                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: palette.inkSoft, mb: 1.5 }}>
                                    All Regions
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                    {primaryRegion !== 'N/A' && (
                                        <Chip
                                            label={primaryRegion}
                                            sx={{
                                                py: 2,
                                                px: 1,
                                                fontSize: '0.85rem',
                                                fontWeight: 600,
                                                bgcolor: `${palette.teal}10`,
                                                color: palette.teal,
                                                border: `1px solid ${palette.teal}20`,
                                                borderRadius: '10px',
                                            }}
                                        />
                                    )}
                                    {secondaryRegion !== 'N/A' && (
                                        <Chip
                                            label={secondaryRegion}
                                            sx={{
                                                py: 2,
                                                px: 1,
                                                fontSize: '0.85rem',
                                                fontWeight: 600,
                                                bgcolor: `${palette.pine}10`,
                                                color: getReadableColor(palette.pine),
                                                border: `1px solid ${palette.pine}20`,
                                                borderRadius: '10px',
                                            }}
                                        />
                                    )}
                                    {/* Legacy support for regions array */}
                                    {regions.length > 0 && regions.map((region, index) => (
                                        <Chip
                                            key={`legacy-region-${index}`}
                                            label={region}
                                            sx={{
                                                py: 2,
                                                px: 1,
                                                fontSize: '0.85rem',
                                                fontWeight: 600,
                                                bgcolor: `${palette.teal}10`,
                                                color: palette.teal,
                                                border: `1px solid ${palette.teal}20`,
                                                borderRadius: '10px',
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {/* Connect With Tab */}
                    {tabValue === 2 && (
                        <Box sx={{ width: '100%' }}>
                            <Typography variant="h6" sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, mb: 2 }}>
                                Connect With
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <InfoCard
                                        icon={<HandshakeIcon fontSize="small" />}
                                        label="Primary Connect With"
                                        value={primaryConnectWith}
                                        color={palette.pine}
                                        iconBg={`${palette.pine}12`}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <InfoCard
                                        icon={<GroupIcon fontSize="small" />}
                                        label="Secondary Connect With"
                                        value={secondaryConnectWith}
                                        color={palette.purple}
                                        iconBg={`${palette.purple}12`}
                                    />
                                </Grid>
                            </Grid>

                            {otherConnectWith && (
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: palette.inkSoft, mb: 1.5 }}>
                                        Other Connect With
                                    </Typography>
                                    <Chip
                                        label={otherConnectWith}
                                        sx={{
                                            py: 2,
                                            px: 1,
                                            fontSize: '0.85rem',
                                            fontWeight: 600,
                                            bgcolor: `${palette.gold}10`,
                                            color: getReadableColor(palette.gold),
                                            border: `1px solid ${palette.gold}20`,
                                            borderRadius: '10px',
                                        }}
                                    />
                                </Box>
                            )}

                            {(primaryConnectWith !== 'N/A' || secondaryConnectWith !== 'N/A' || otherConnectWith) && (
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: palette.inkSoft, mb: 1.5 }}>
                                        All Connect With Interests
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                        {primaryConnectWith !== 'N/A' && (
                                            <Chip
                                                label={primaryConnectWith}
                                                sx={{
                                                    py: 2,
                                                    px: 1,
                                                    fontSize: '0.85rem',
                                                    fontWeight: 600,
                                                    bgcolor: `${palette.pine}10`,
                                                    color: getReadableColor(palette.pine),
                                                    border: `1px solid ${palette.pine}20`,
                                                    borderRadius: '10px',
                                                }}
                                            />
                                        )}
                                        {secondaryConnectWith !== 'N/A' && (
                                            <Chip
                                                label={secondaryConnectWith}
                                                sx={{
                                                    py: 2,
                                                    px: 1,
                                                    fontSize: '0.85rem',
                                                    fontWeight: 600,
                                                    bgcolor: `${palette.purple}10`,
                                                    color: palette.purple,
                                                    border: `1px solid ${palette.purple}20`,
                                                    borderRadius: '10px',
                                                }}
                                            />
                                        )}
                                        {otherConnectWith && (
                                            <Chip
                                                label={otherConnectWith}
                                                sx={{
                                                    py: 2,
                                                    px: 1,
                                                    fontSize: '0.85rem',
                                                    fontWeight: 600,
                                                    bgcolor: `${palette.gold}10`,
                                                    color: getReadableColor(palette.gold),
                                                    border: `1px solid ${palette.gold}20`,
                                                    borderRadius: '10px',
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Contact Tab */}
                    {tabValue === 3 && (
                        <Box sx={{ width: '100%' }}>
                            <Typography variant="h6" sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, mb: 2 }}>
                                Contact Information
                            </Typography>
                            <Grid container spacing={2}>
                                {email && email !== 'N/A' && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <InfoCard
                                            icon={<EmailIcon fontSize="small" />}
                                            label="Email"
                                            value={email}
                                            color={palette.rust}
                                        />
                                    </Grid>
                                )}
                                {phone && phone !== 'N/A' && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <InfoCard
                                            icon={<PhoneIcon fontSize="small" />}
                                            label="Phone"
                                            value={dialCode ? `${dialCode} ${phone}` : phone}
                                            color={palette.teal}
                                        />
                                    </Grid>
                                )}
                                {website && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <InfoCard
                                            icon={<LanguageIcon fontSize="small" />}
                                            label="Website"
                                            value={
                                                <a href={safeExternalUrl(website, { allowBlob: false }) || undefined} target="_blank" rel="noopener noreferrer" style={{ color: getReadableColor(palette.rust), textDecoration: 'none' }}>
                                                    {website}
                                                </a>
                                            }
                                            color={palette.purple}
                                        />
                                    </Grid>
                                )}
                                {country && country !== 'N/A' && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <InfoCard
                                            icon={<LocationIcon fontSize="small" />}
                                            label="Country"
                                            value={country}
                                            color={palette.pine}
                                        />
                                    </Grid>
                                )}
                                {dialCode && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <InfoCard
                                            icon={<PhoneIcon fontSize="small" />}
                                            label="Dial Code"
                                            value={dialCode}
                                            color={palette.amber}
                                        />
                                    </Grid>
                                )}
                                {linkedin && (
                                    <Grid size={{ xs: 12 }}>
                                        <InfoCard
                                            icon={<LinkedInIcon fontSize="small" />}
                                            label="LinkedIn"
                                            value={
                                                <a href={safeExternalUrl(linkedin, { allowBlob: false }) || undefined} target="_blank" rel="noopener noreferrer" style={{ color: getReadableColor(palette.rust), textDecoration: 'none' }}>
                                                    View Profile
                                                </a>
                                            }
                                            color={palette.blue}
                                        />
                                    </Grid>
                                )}
                            </Grid>

                            {(email === 'N/A' && phone === 'N/A' && !website) && (
                                <Typography variant="body2" sx={{ color: palette.inkSoft }}>
                                    No contact information available.
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* Associated Users Tab */}
                    {tabValue === 4 && (
                        <Box sx={{ width: '100%' }}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between', mb: 2 }}>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 850, color: palette.ink }}>Associated Users</Typography>
                                    <Typography sx={{ fontSize: '0.75rem', color: palette.inkLight }}>Professionals registered with {companyName}. Open a profile to review business interests and send an AI introduction directly.</Typography>
                                </Box>
                                <Chip icon={<PeopleIcon />} label={`${associatedUsers.length} active users`} sx={{ bgcolor: `${palette.purple}10`, color: getReadableColor(palette.purple), fontWeight: 800 }} />
                            </Stack>
                            {associatedUsers.length ? (
                                <Grid container spacing={1.5}>
                                    {associatedUsers.map((member, memberIndex) => {
                                        const memberName = String(member.displayName || member.name || [member.firstName, member.lastName].filter(Boolean).join(' ') || member.email || 'TELCOFAIR User');
                                        const colors = [palette.blue, palette.purple, palette.pine, palette.gold, palette.teal, palette.rust];
                                        const memberColor = colors[memberIndex % colors.length];
                                        const memberBusiness = userBusinessProfile(member, company);
                                        return (
                                            <Grid key={member.id} size={{ xs: 12, sm: 6, lg: 4 }}>
                                                <Paper elevation={0} className="tf-associated-user-card tf-company-person-card" sx={{ '--member-color': memberColor, overflow: 'hidden', pt: 0 }}>
                                                    <Box sx={{ height: 82, mx: -1.5, mt: -1.5, mb: 0, position: 'relative', overflow: 'hidden', background: coverImage ? `linear-gradient(180deg,rgba(20,36,52,.04),rgba(20,36,52,.22)), url("${coverImage}") center/cover no-repeat` : `linear-gradient(118deg,${memberColor},${palette.purple})` }}>
                                                        {!coverImage && <PeopleIcon sx={{ position:'absolute', right:9, bottom:-20, fontSize:72, color:'rgba(255,255,255,.14)', transform:'rotate(-10deg)' }} />}
                                                    </Box>
                                                    <Box className="tf-company-person-art"><PeopleIcon /></Box>
                                                    <Stack direction="row" spacing={1.1} sx={[{ alignItems: 'center' }, { position: 'relative', zIndex: 2, mt: -3.1 }]}>
                                                        <Avatar src={getPersonPhotoUrl(member)} sx={{ width: 54, height: 54, borderRadius: '15px', bgcolor: memberColor, border: '3px solid rgba(255,255,255,.98)', boxShadow: `0 10px 22px ${memberColor}2B` }}>{memberName.charAt(0).toUpperCase()}</Avatar>
                                                        <Box sx={{ minWidth: 0, flex: 1, pt: 2.4 }}>
                                                            <Typography noWrap sx={{ fontWeight: 900, color: palette.ink, fontSize: '0.84rem' }}>{memberName}</Typography>
                                                            <Typography noWrap sx={{ fontSize: '0.7rem', color: palette.inkSoft, fontWeight: 700 }}>{memberBusiness.jobTitle || 'Business Professional'}</Typography>
                                                            <Stack direction="row" spacing={.45} useFlexGap sx={{ flexWrap: 'wrap', mt: .5 }}>
                                                                {memberBusiness.country && <Chip size="small" label={memberBusiness.country} sx={{ height: 21, fontSize: '0.58rem', bgcolor: 'rgba(255,255,255,.72)' }} />}
                                                                {memberBusiness.specialization?.[0] && <Chip size="small" label={memberBusiness.specialization[0]} sx={{ height: 21, fontSize: '0.58rem', bgcolor: `${memberColor}0D`, color: getReadableColor(memberColor) }} />}
                                                            </Stack>
                                                        </Box>
                                                    </Stack>
                                                    <Grid container spacing={.65} sx={{ mt: .85, position: 'relative', zIndex: 2 }}>
                                                        <Grid size={{ xs: 6 }}><Box className="tf-company-person-mini"><Typography>Primary service</Typography><strong>{compactList(memberBusiness.primaryServices, 1) || 'Not specified'}</strong></Box></Grid>
                                                        <Grid size={{ xs: 6 }}><Box className="tf-company-person-mini"><Typography>Looking for</Typography><strong>{compactList([...memberBusiness.primaryNeeds, ...memberBusiness.secondaryNeeds], 1) || 'Not specified'}</strong></Box></Grid>
                                                        <Grid size={{ xs: 12 }}><Box className="tf-company-person-mini"><Typography>Service regions</Typography><strong>{compactList([...memberBusiness.primaryRegions, ...memberBusiness.secondaryRegions], 2) || 'Not specified'}</strong></Box></Grid>
                                                    </Grid>
                                                    <Stack direction="row" spacing={.7} sx={{ mt: 1.05, position: 'relative', zIndex: 2 }}>
                                                        <Button fullWidth startIcon={<VisibilityIcon />} onClick={() => navigate(`/dashboard/users/${member.id}`)} variant="outlined" sx={{ minHeight: 33, borderRadius: '10px', textTransform: 'none', fontSize: '0.64rem', fontWeight: 850, color: getReadableColor(memberColor), borderColor: `${memberColor}28` }}>Profile</Button>
                                                        <Button fullWidth startIcon={<AiIcon />} onClick={() => navigate(`/dashboard/users/${member.id}`, { state: { openAiIntroduction: true } })} variant="contained" sx={{ minHeight: 33, borderRadius: '10px', textTransform: 'none', fontSize: '0.64rem', fontWeight: 900, color: '#fff !important', background: `linear-gradient(135deg,${memberColor},${palette.purple})` }}>AI Intro</Button>
                                                        <EntityRatingButton compact targetType="user" targetId={member.id} targetName={memberName} target={member} color="#E9A220" />
                                                    </Stack>
                                                </Paper>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            ) : (
                                <Box className="erp-empty-state" sx={{ py: 5 }}>
                                    <PeopleIcon sx={{ fontSize: 40, color: palette.inkMuted }} />
                                    <Typography sx={{ mt: 1, fontWeight: 800, color: palette.ink }}>No associated users found</Typography>
                                    <Typography sx={{ fontSize: '0.74rem', color: palette.inkLight }}>Users linked to this company will appear here automatically.</Typography>
                                </Box>
                            )}
                        </Box>
                    )}

                </Paper>
            </Container>

            <style>
                {`
                    @keyframes pulse {
                        0%, 100% { opacity: 1; transform: scale(1); }
                        50% { opacity: 0.5; transform: scale(0.8); }
                    }
                    @keyframes float {
                        0%, 100% { transform: translate(0, 0) rotate(0deg); }
                        50% { transform: translate(-10px, -10px) rotate(5deg); }
                    }
                    @keyframes shimmerRule {
                        0%, 100% { opacity: 0.7; width: 34px; }
                        50% { opacity: 1; width: 48px; }
                    }
                    @keyframes shimmerEdge {
                        0% { background-position: 0% 0%; }
                        100% { background-position: 300% 0%; }
                    }
                `}
            </style>
        </Box>
    );
};

export default CompanyProfile;